import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { PlayerError } from 'screens/PlayerModal/types';
import { onBeforeShowPlayerModal } from 'screens/PlayerModal/utils';

type Params = {
    fullscreen?: boolean;
    embedded?: boolean;
    onSetPlayerError?(error: PlayerError, videoSessionId: string): void;
    onResetPlayerError?(): void;
};

const {
    PLAYER_LOCKED_CONTENT_OVERLAY_TITLE,
    PLAYER_LOCKED_CONTENT_OVERLAY_DESCRIPTION_RN,
} = LANGUAGE_KEYS;

export default function useParentalControl({
    fullscreen = false,
    onSetPlayerError,
    onResetPlayerError,
}: Params = {}) {
    const [showPinDialog, setShowPinDialog] = useState(false);

    const { t } = useTranslation();

    const onShowPinDialog = useCallback(() => {
        setShowPinDialog(true);
    }, []);

    const onHidePinDialog = useCallback(() => {
        setShowPinDialog(false);
    }, []);

    const onParentalCheckDismiss = useCallback(
        (shouldSetPlayerError: boolean = true) => {
            onHidePinDialog();

            if (shouldSetPlayerError && onSetPlayerError) {
                onSetPlayerError(
                    {
                        message: t(PLAYER_LOCKED_CONTENT_OVERLAY_TITLE),
                        description: t(
                            PLAYER_LOCKED_CONTENT_OVERLAY_DESCRIPTION_RN,
                        ),
                        withIcon: false,
                    },
                    '',
                );

                if (fullscreen) {
                    onBeforeShowPlayerModal();
                }
            }
        },
        [fullscreen, onHidePinDialog, onSetPlayerError],
    );

    const onParentalControlChecked = useCallback(() => {
        onResetPlayerError && onResetPlayerError();
        setShowPinDialog(false);

        if (fullscreen) {
            setTimeout(() => {
                onBeforeShowPlayerModal();
            }, 150);
        }
    }, [fullscreen, onResetPlayerError]);

    return {
        showPinDialog,
        onShowPinDialog,
        onParentalCheckDismiss,
        onParentalControlChecked,
    };
}
